<template>
    <div class="weeduc-admin-page corporate-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.collaborators.title"
                    :subtitle="t.collaborators.subtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions" v-if="canManage">
                <v-btn
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none me-2"
                    @click="$router.push('/dashboard/corporativo/importar-colaboradores')"
                >
                    <v-icon class="me-1" size="small">mdi-upload</v-icon>
                    {{ t.collaborators.import }}
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    @click="openCreate()"
                >
                    <v-icon class="me-1" size="small">mdi-plus</v-icon>
                    {{ t.collaborators.new }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <section class="corporate-kpi-grid" :aria-label="t.common.summary">
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.total }}</strong>
                    <span class="corporate-kpi__label">{{ t.collaborators.totalsAll }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.active }}</strong>
                    <span class="corporate-kpi__label">{{ t.collaborators.totalsActive }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.responsible }}</strong>
                    <span class="corporate-kpi__label">{{ t.collaborators.totalsResponsible }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.avgProgress }}%</strong>
                    <span class="corporate-kpi__label">{{ t.collaborators.avgProgress }}</span>
                </article>
            </section>

            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar">
                    <CollaboratorFilters
                        :filters="filters"
                        :labels="t.collaborators"
                        :sector-options="sectorOptions"
                        :role-options="roleOptions"
                        :show-type="false"
                        @update:filters="onFiltersChange"
                    />
                </div>

                <div class="weeduc-admin-page__table-section">
                    <div v-if="loading" class="corporate-placeholder">
                        <Loader :isActive="false" />
                    </div>
                    <div v-else-if="error" class="corporate-placeholder">{{ t.common.error }}</div>
                    <template v-else>
                        <CollaboratorTable
                            :items="users"
                            :labels="tableLabels"
                            :empty-text="t.collaborators.empty"
                            :active-label="t.collaborators.active"
                            :inactive-label="t.collaborators.inactive"
                            @edit="goEdit"
                            @change-org="openMove"
                            @view-performance="goDetail"
                            @remove="openRemove"
                        />
                        <div
                            v-if="totalElements > 0"
                            class="weeduc-admin-page__pagination corporate-collaborators-pagination"
                        >
                            <v-select
                                v-model="pageSize"
                                :items="pageSizeOptions"
                                :label="t.collaborators.perPage"
                                variant="outlined"
                                density="compact"
                                hide-details
                                class="weeduc-admin-v-field corporate-collaborators-pagination__size"
                                @update:model-value="onPageSizeChange"
                            />
                            <span class="corporate-collaborators-pagination__info">
                                {{ paginationInfo }}
                            </span>
                            <v-pagination
                                v-model="currentPage"
                                :length="totalPages"
                                :total-visible="5"
                                density="comfortable"
                                @update:model-value="onPageChange"
                            />
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <MoveUserDialog
            v-model="moveOpen"
            :user="selectedUser"
            :labels="t.collaborators"
            :common="t.common"
            :board-options="[]"
            :sector-options="sectorOptions.filter((o) => o.value)"
            :role-options="roleOptions.filter((o) => o.value)"
            :sectors-raw="sectorsRaw"
            :roles-raw="rolesRaw"
            :saving="saving"
            @submit="saveOrganization"
        />

        <v-dialog v-model="createOpen" max-width="860" scrollable>
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">
                            {{ createUserType === CORPORATE_USER_TYPE.GESTOR ? t.collaborators.formTitleCreateManager : t.collaborators.formTitleCreate }}
                        </h2>
                        <p class="corporate-dialog__subtitle">{{ t.collaborators.formSubtitle }}</p>
                    </div>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <div class="corporate-collaborator-form__demo">
                        <v-btn
                            variant="tonal"
                            color="primary"
                            class="text-none"
                            rounded="lg"
                            prepend-icon="mdi-auto-fix"
                            @click="fillCreateDemoData"
                        >
                            Preencher dados
                        </v-btn>
                    </div>
                    <CollaboratorFormFields
                        v-model="createForm"
                        :labels="t.collaborators"
                        :sector-options="sectorOptions"
                        :role-options="roleOptions"
                        :roles-raw="rolesRaw"
                        :status-options="createStatusOptions"
                        :country-options="countryOptions"
                        :brazilian-states="brazilianStates"
                        :photo-file="createPhotoFile"
                        :photo-preview="createPhotoPreview"
                        :loading-address="loadingAddress"
                        show-activation-note
                        :show-org-section="createUserType !== CORPORATE_USER_TYPE.GESTOR"
                        @photo-change="onCreatePhotoChange"
                        @photo-clear="clearCreatePhoto"
                        @cep-change="getCreateCep"
                        @sector-change="onCreateSectorChange"
                    />
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn variant="text" class="text-none" @click="createOpen = false">
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="saving"
                        @click="saveCreate"
                    >
                        {{ t.common.save }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="saveErrorOpen" max-width="480">
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <h2 class="corporate-dialog__title">{{ errorDialogTitle || t.collaborators.saveErrorTitle }}</h2>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <p class="corporate-dialog__hint mb-0">{{ saveErrorMessage }}</p>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        @click="saveErrorOpen = false"
                    >
                        {{ t.common.close }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="removeOpen" max-width="480" persistent>
            <v-card rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <h2 class="corporate-dialog__title">{{ t.collaborators.delete }}</h2>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <p class="corporate-dialog__hint mb-0">{{ t.collaborators.deleteConfirm }}</p>
                    <p v-if="removeTarget?.name" class="corporate-dialog__user-line mt-3 mb-0">
                        {{ removeTarget.name }}
                    </p>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn
                        variant="text"
                        class="text-none"
                        :disabled="removing"
                        @click="closeRemove"
                    >
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="error"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="removing"
                        @click="executeRemove"
                    >
                        {{ t.collaborators.delete }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import CollaboratorFilters from '@/components/Admin/Corporate/CollaboratorFilters.vue';
import CollaboratorTable from '@/components/Admin/Corporate/CollaboratorTable.vue';
import CollaboratorFormFields from '@/components/Admin/Corporate/CollaboratorFormFields.vue';
import MoveUserDialog from '@/components/Admin/Corporate/MoveUserDialog.vue';
import usersApi, {
    CORPORATE_USER_TYPE,
    isCorporateManagerType,
} from '@/utils/corporate/users';
import organizationalUnitsApi from '@/utils/corporate/organizationalUnits';
import rolesApi from '@/utils/corporate/roles';
import { canManageCorporateStructure } from '@/utils/corporate/permissions';
import { corporateI18n } from '@/i18n/mixins';
import brazilianStates from '@/mocks/brazilianStates';
import apiCep from '@/utils/cep';
import apiAWS from '@/utils/storage/aws';
import DefaultProfileImg from '@/assets/user.png';
import { mapState } from 'vuex';

function apiErrorMessage(error, fallback = 'Erro inesperado.') {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && data.message) return data.message;
    if (error?.message) return error.message;
    return fallback;
}

export default {
    name: 'CorporateCollaborators',
    mixins: [corporateI18n],
    components: {
        TitlePage,
        Loader,
        CollaboratorFilters,
        CollaboratorTable,
        CollaboratorFormFields,
        MoveUserDialog,
    },
    data() {
        return {
            CORPORATE_USER_TYPE,
            loading: true,
            error: false,
            saving: false,
            users: [],
            totalElements: 0,
            currentPage: 1,
            pageSize: 20,
            pageSizeOptions: [10, 20, 50],
            listSummary: {
                active: 0,
                responsible: 0,
                avgProgress: 0,
            },
            filters: {
                search: '',
                sectorId: null,
                roleId: null,
                isResponsible: null,
                status: null,
                type: null,
            },
            sectorOptions: [],
            roleOptions: [],
            managerOptions: [],
            sectorsRaw: [],
            rolesRaw: [],
            moveOpen: false,
            createOpen: false,
            createUserType: CORPORATE_USER_TYPE.COLABORADOR,
            selectedUser: null,
            debounceTimer: null,
            loadingAddress: false,
            saveErrorOpen: false,
            saveErrorMessage: '',
            errorDialogTitle: '',
            removeOpen: false,
            removeTarget: null,
            removing: false,
            createPhotoFile: null,
            createPhotoPreviewUrl: '',
            brazilianStates,
            createForm: {
                name: '',
                email: '',
                photo: '',
                socialNumber: '',
                idNumber: '',
                phone: '',
                postcode: '',
                address: '',
                addressNumber: '',
                addressComplement: '',
                neighborhood: '',
                city: '',
                county: 'AC',
                country: 'br',
                sectorId: null,
                roleId: null,
                managerUserId: null,
                admissionDate: null,
                internalCode: '',
                isResponsible: false,
                status: 'active',
                type: CORPORATE_USER_TYPE.COLABORADOR,
            },
        };
    },
    computed: {
        ...mapState(['token']),
        canManage() {
            return canManageCorporateStructure();
        },
        tableLabels() {
            return {
                ...this.t.collaborators,
                yes: this.t.users?.yes || 'Sim',
                no: this.t.users?.no || 'Não',
                responsible: this.t.collaborators.responsible,
                edit: this.t.collaborators.edit,
            };
        },
        createStatusOptions() {
            return [
                { text: this.t.collaborators.active, value: 'active' },
                { text: this.t.collaborators.inactive, value: 'inactive' },
            ];
        },
        countryOptions() {
            return [
                { text: 'Brasil', value: 'br' },
                { text: 'Outro', value: 'outro' },
            ];
        },
        createPhotoPreview() {
            if (this.createPhotoPreviewUrl) return this.createPhotoPreviewUrl;
            if (typeof this.createForm.photo === 'string' && this.createForm.photo) {
                return this.createForm.photo;
            }
            return DefaultProfileImg;
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.totalElements / this.pageSize));
        },
        paginationInfo() {
            if (!this.totalElements) return '';
            const from = (this.currentPage - 1) * this.pageSize + 1;
            const to = Math.min(this.currentPage * this.pageSize, this.totalElements);
            const template = this.t.collaborators.showingRange || 'Mostrando {from}–{to} de {total}';
            return template
                .replace('{from}', from)
                .replace('{to}', to)
                .replace('{total}', this.totalElements);
        },
        stats() {
            return {
                total: this.totalElements,
                active: this.listSummary.active,
                responsible: this.listSummary.responsible,
                avgProgress: this.listSummary.avgProgress,
            };
        },
    },
    async created() {
        await this.loadLookups();
        await this.loadUsers();
    },
    methods: {
        applyRoleOptions(roles = [], { includeAll = true } = {}) {
            this.rolesRaw = roles;
            const mapped = roles.map((r) => ({ text: r.name, value: r.id }));
            this.roleOptions = includeAll
                ? [{ text: this.t.collaborators.all, value: null }, ...mapped]
                : mapped;
        },
        async loadLookups() {
            const soft = async (promise, fallback) => {
                try {
                    return await promise;
                } catch (e) {
                    console.warn(e);
                    return fallback;
                }
            };
            // Setores: GET /corporate/organizational-units?type=sector
            // Cargos: GET /corporate/roles
            const loadSectors = async () => {
                try {
                    return await organizationalUnitsApi.listSectors(this.token);
                } catch (e) {
                    console.warn('[Collaborators] setores com status=active falhou, retry', e);
                    return organizationalUnitsApi.list(this.token, { type: 'sector' });
                }
            };
            const [sectors, roles, managers] = await Promise.all([
                soft(loadSectors(), { content: [], managers: [] }),
                soft(rolesApi.list(this.token), { content: [] }),
                soft(usersApi.listManagers(this.token), { content: [] }),
            ]);
            this.sectorsRaw = sectors.content || [];
            this.sectorOptions = [
                { text: this.t.collaborators.all, value: null },
                ...(sectors.content || []).map((s) => ({ text: s.name, value: s.id })),
            ];
            this.applyRoleOptions(roles.content || []);
            const managerList = managers.content?.length
                ? managers.content
                : (sectors.managers || []);
            this.managerOptions = [
                { text: this.t.collaborators.all, value: null },
                ...managerList.map((m) => ({ text: m.name, value: m.id })),
            ];
        },
        async onCreateSectorChange(sectorId) {
            try {
                const res = await rolesApi.list(this.token, {
                    sectorId: sectorId || undefined,
                });
                this.applyRoleOptions(res.content || [], { includeAll: false });
            } catch (e) {
                console.warn(e);
            }
        },
        async loadUsers() {
            this.loading = true;
            this.error = false;
            try {
                const res = await usersApi.list(this.token, {
                    ...this.filters,
                    search: this.filters.search || undefined,
                    type: CORPORATE_USER_TYPE.COLABORADOR,
                    page: this.currentPage - 1,
                    size: this.pageSize,
                });
                this.users = res.content || [];
                this.totalElements = Number(res.totalElements ?? this.users.length);
                this.listSummary = {
                    active: Number(res.summary?.active ?? 0),
                    responsible: Number(res.summary?.responsible ?? 0),
                    avgProgress: Number(res.summary?.avgProgress ?? 0),
                };
                const maxPage = Math.max(1, Math.ceil(this.totalElements / this.pageSize) || 1);
                if (this.currentPage > maxPage) {
                    this.currentPage = maxPage;
                    if (this.totalElements > 0) {
                        await this.loadUsers();
                    }
                }
            } catch (e) {
                console.error(e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        onFiltersChange(next) {
            this.filters = next;
            this.currentPage = 1;
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.loadUsers(), 300);
        },
        onPageChange() {
            this.loadUsers();
        },
        onPageSizeChange() {
            this.currentPage = 1;
            this.loadUsers();
        },
        emptyCreateForm(userType = CORPORATE_USER_TYPE.COLABORADOR) {
            const type = isCorporateManagerType(userType)
                ? CORPORATE_USER_TYPE.GESTOR
                : CORPORATE_USER_TYPE.COLABORADOR;
            return {
                name: '',
                email: '',
                photo: '',
                socialNumber: '',
                idNumber: '',
                phone: '',
                postcode: '',
                address: '',
                addressNumber: '',
                addressComplement: '',
                neighborhood: '',
                city: '',
                county: 'AC',
                country: 'br',
                sectorId: null,
                roleId: null,
                managerUserId: null,
                admissionDate: null,
                internalCode: '',
                isResponsible: false,
                status: 'active',
                type,
            };
        },
        async openCreate(userType = CORPORATE_USER_TYPE.COLABORADOR) {
            this.createUserType = isCorporateManagerType(userType)
                ? CORPORATE_USER_TYPE.GESTOR
                : CORPORATE_USER_TYPE.COLABORADOR;
            this.clearCreatePhoto();
            this.createPhotoFile = null;
            this.createForm = this.emptyCreateForm(this.createUserType);
            this.createOpen = true;
            // Recarrega cargos sem filtro de setor
            try {
                const res = await rolesApi.list(this.token);
                this.applyRoleOptions(res.content || []);
            } catch (e) {
                console.warn(e);
            }
        },
        fillCreateDemoData() {
            const stamp = Date.now().toString().slice(-5);
            const sector = this.sectorsRaw.find((s) => s.status !== 'inactive') || this.sectorsRaw[0];
            const rolesForSector = sector
                ? this.rolesRaw.filter((r) => r.sectorId === sector.id && r.status !== 'inactive')
                : [];
            const role = rolesForSector[0]
                || this.rolesRaw.find((r) => r.status !== 'inactive')
                || this.rolesRaw[0];

            const isManager = isCorporateManagerType(this.createUserType);
            this.createForm = {
                ...this.emptyCreateForm(this.createUserType),
                name: isManager ? `Gestor Demo ${stamp}` : `Colaborador Demo ${stamp}`,
                email: `samuel.linkon+teste${stamp}@tutor.do`,
                socialNumber: '529.982.247-25',
                idNumber: `MG-${stamp}`,
                phone: '(31) 98888-1234',
                postcode: '30130-010',
                address: 'Avenida Afonso Pena',
                addressNumber: '1500',
                addressComplement: 'Sala 12',
                neighborhood: 'Centro',
                city: 'Belo Horizonte',
                county: 'MG',
                country: 'br',
                sectorId: isManager ? null : (sector?.id || null),
                roleId: isManager ? null : (role?.id || null),
                isResponsible: false,
                status: 'active',
                type: isManager
                    ? CORPORATE_USER_TYPE.GESTOR
                    : CORPORATE_USER_TYPE.COLABORADOR,
            };
        },
        onCreatePhotoChange(value) {
            const file = Array.isArray(value) ? value[0] : value;
            if (this.createPhotoPreviewUrl) {
                URL.revokeObjectURL(this.createPhotoPreviewUrl);
                this.createPhotoPreviewUrl = '';
            }
            this.createPhotoFile = file instanceof File ? file : null;
            if (file instanceof File) {
                this.createPhotoPreviewUrl = URL.createObjectURL(file);
                this.createForm.photo = file;
            } else {
                this.createForm.photo = '';
            }
        },
        clearCreatePhoto() {
            if (this.createPhotoPreviewUrl) {
                URL.revokeObjectURL(this.createPhotoPreviewUrl);
                this.createPhotoPreviewUrl = '';
            }
            this.createPhotoFile = null;
            this.createForm.photo = '';
        },
        async getCreateCep() {
            if (!this.createForm.postcode) return;
            this.loadingAddress = true;
            try {
                const response = await apiCep.getDataCep(this.createForm.postcode);
                this.createForm.address = response.logradouro || this.createForm.address;
                this.createForm.neighborhood = response.bairro || this.createForm.neighborhood;
                this.createForm.city = response.localidade || this.createForm.city;
                this.createForm.county = response.uf || this.createForm.county;
            } catch (e) {
                alert('Erro ao buscar o CEP. Verifique o valor ou preencha o endereço manualmente.');
            } finally {
                this.loadingAddress = false;
            }
        },
        async resolveCreatePhoto() {
            const photo = this.createForm.photo;
            if (typeof photo === 'string') {
                if (photo.startsWith('data:')) {
                    throw new Error('A foto deve ser enviada ao bucket, não em base64.');
                }
                return photo;
            }
            if (!(photo instanceof File)) return '';

            const signature = await apiAWS.getSignatureImage(this.token, photo.name);
            if (!signature?.url || !signature?.token) {
                throw new Error('Não foi possível obter URL assinada para a foto.');
            }
            await apiAWS.putFileAWS(signature.url, photo);
            return signature.token;
        },
        openMove(user) {
            this.selectedUser = user;
            this.moveOpen = true;
        },
        goDetail(user) {
            this.$router.push(`/dashboard/corporativo/colaboradores/${user.id}`);
        },
        goEdit(user) {
            this.$router.push(`/dashboard/corporativo/colaboradores/${user.id}/editar`);
        },
        async saveCreate() {
            if (!this.createForm.name?.trim() || !this.createForm.email?.trim()) return;
            this.saving = true;
            try {
                const photo = await this.resolveCreatePhoto();
                const isManager = isCorporateManagerType(this.createUserType)
                    || isCorporateManagerType(this.createForm.type);
                const payload = {
                    ...this.createForm,
                    photo: photo || '',
                    type: isManager
                        ? CORPORATE_USER_TYPE.GESTOR
                        : CORPORATE_USER_TYPE.COLABORADOR,
                };
                if (isManager) {
                    payload.type = CORPORATE_USER_TYPE.GESTOR;
                    payload.sectorId = null;
                    payload.roleId = null;
                    payload.admissionDate = null;
                    payload.internalCode = '';
                    payload.managerUserId = null;
                    payload.isResponsible = false;
                    payload.manager = 0;
                }
                await usersApi.create(this.token, payload);
                this.createOpen = false;
                this.clearCreatePhoto();
                await this.loadLookups();
                await this.loadUsers();
            } catch (e) {
                this.showSaveError(e);
            } finally {
                this.saving = false;
            }
        },
        showSaveError(error) {
            this.errorDialogTitle = this.t.collaborators.saveErrorTitle;
            this.saveErrorMessage = apiErrorMessage(error, this.t.common.error);
            this.saveErrorOpen = true;
        },
        openRemove(user) {
            this.removeTarget = user;
            this.removeOpen = true;
        },
        closeRemove() {
            if (this.removing) return;
            this.removeOpen = false;
            this.removeTarget = null;
        },
        async executeRemove() {
            if (!this.removeTarget) return;
            this.removing = true;
            try {
                await usersApi.remove(this.token, this.removeTarget.id);
                this.removeOpen = false;
                this.removeTarget = null;
                await this.loadUsers();
            } catch (e) {
                this.removeOpen = false;
                this.errorDialogTitle = this.t.collaborators.deleteErrorTitle;
                this.saveErrorMessage = apiErrorMessage(e, this.t.common.error);
                this.saveErrorOpen = true;
            } finally {
                this.removing = false;
            }
        },
        async saveOrganization(payload) {
            if (!this.selectedUser) return;
            this.saving = true;
            try {
                await usersApi.updateOrganization(this.token, this.selectedUser.id, payload);
                this.moveOpen = false;
                await this.loadUsers();
            } catch (e) {
                alert(apiErrorMessage(e, this.t.common.error));
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-collaborator-form__demo {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 1rem;
}

.corporate-collaborators-pagination {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
}

.corporate-collaborators-pagination__size {
    max-width: 7.5rem;
    flex: 0 0 auto;
}

.corporate-collaborators-pagination__info {
    color: #6b7280;
    font-size: 0.85rem;
    flex: 1 1 auto;
}

.corporate-collaborators-pagination :deep(.v-pagination) {
    flex: 0 0 auto;
    margin-left: auto;
}
</style>
